import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import { RootState } from "src/redux/reducers";
import {
  clearAIChatHelpCenterUrl,
  clearAIChatPredefinedPrompt,
  openAIChatWithHelpCenterUrl,
} from "src/redux/ai-chat/aiChatActions";
import { useAIChat } from "./hooks/useAIChat";
import ChatToolbar from "./components/ChatToolbar/ChatToolbar";
import ChatMessageList from "./components/ChatMessageList/ChatMessageList";
import ChatInput from "./components/ChatInput/ChatInput";
import Loader from "src/app/components/Loader/Loader";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { appendMemberUuidToUrl } from "src/utils/internalServices";

import "./AIChat.scss";

const HELP_CENTER_CLOSE_ANIMATION_MS = 220;

function AIChatContent({
  isOpen,
  isWide,
  onToggleWide,
}: {
  isOpen: boolean;
  isWide: boolean;
  onToggleWide: () => void;
}) {
  const dispatch = useDispatch();
  const {
    messages,
    isLoading,
    isUploading,
    isConversationArchived,
    sendMessage,
    abortRequest,
    loadConversation,
    startNewChat,
    getCurrentConversationId,
    selectedConversationId,
    setSelectedConversationId,
  } = useAIChat();
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const pendingPrompt = useSelector(
    (state: RootState) => state.aiChatReducer.predefinedPrompt,
  );
  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );
  const helpCenterUrl = useSelector(
    (state: RootState) => state.aiChatReducer.helpCenterUrl,
  );
  const [isHelpCenterClosing, setIsHelpCenterClosing] = useState(false);
  const closeHelpCenterTimeoutRef = useRef<number | undefined>(undefined);

  const resolvedHelpCenterUrl = useMemo(() => {
    if (!helpCenterUrl) {
      return null;
    }

    return appendMemberUuidToUrl(helpCenterUrl, identity?.id ?? null);
  }, [helpCenterUrl, identity?.id]);

  useEffect(() => {
    if (!pendingPrompt) return;
    dispatch(clearAIChatPredefinedPrompt());
    startNewChat();
    sendMessage(pendingPrompt.chatInput, [], pendingPrompt.predefinedPrompt);
  }, [pendingPrompt, dispatch, sendMessage, startNewChat]);

  const handleQuickPrompt = () => {
    sendMessage("Show all in Creator Search");
  };

  const handleHelpCenterLinkClick = (url: string) => {
    window.clearTimeout(closeHelpCenterTimeoutRef.current);
    setIsHelpCenterClosing(false);
    dispatch(openAIChatWithHelpCenterUrl(url));
  };

  const handleCloseHelpCenter = () => {
    if (isHelpCenterClosing) return;

    setIsHelpCenterClosing(true);
    closeHelpCenterTimeoutRef.current = window.setTimeout(() => {
      dispatch(clearAIChatHelpCenterUrl());
      setIsHelpCenterClosing(false);
    }, HELP_CENTER_CLOSE_ANIMATION_MS);
  };

  const handleStartNewChat = () => {
    dispatch(clearAIChatHelpCenterUrl());
    startNewChat();
  };

  const handleConversationLoad: typeof loadConversation = (...args) => {
    dispatch(clearAIChatHelpCenterUrl());
    loadConversation(...args);
  };

  useEffect(() => {
    return () => window.clearTimeout(closeHelpCenterTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (helpCenterUrl) return;

    window.clearTimeout(closeHelpCenterTimeoutRef.current);
    setIsHelpCenterClosing(false);
  }, [helpCenterUrl]);

  const showHelpCenter = Boolean(resolvedHelpCenterUrl);

  return (
    <section
      className={classNames("ai-chat", {
        "ai-chat--open": isOpen,
        "ai-chat--wide": isWide,
      })}
    >
      <ChatToolbar
        onConversationLoad={handleConversationLoad}
        onLoadingConversation={setIsLoadingConversation}
        onClearConversation={handleStartNewChat}
        getCurrentConversationId={getCurrentConversationId}
        selectedConversationId={selectedConversationId}
        setSelectedConversationId={setSelectedConversationId}
        isWide={isWide}
        onToggleWide={onToggleWide}
      />
      <div className="ai-chat__content">
        {isLoadingConversation ? (
          <div className="ai-chat__loader-container">
            <Loader />
          </div>
        ) : (
          <>
            <ChatMessageList
              messages={messages}
              isLoading={isLoading}
              isArchived={isConversationArchived}
              onQuickPrompt={handleQuickPrompt}
              onHelpCenterLinkClick={handleHelpCenterLinkClick}
            />
            {showHelpCenter ? (
              <div
                className={classNames("ai-chat__help-center-wrapper", {
                  "ai-chat__help-center-wrapper--closing": isHelpCenterClosing,
                })}
              >
                <div className="ai-chat__help-center-header">
                  <button
                    type="button"
                    className="ai-chat__help-center-back-btn"
                    onClick={handleCloseHelpCenter}
                  >
                    <IDHFormattedMessage id="ws_close" defaultMessage="Close" />
                  </button>
                </div>
                <iframe
                  title="Help Center"
                  src={resolvedHelpCenterUrl || undefined}
                  className="ai-chat__help-center-iframe"
                />
              </div>
            ) : (
              <div className="ai-chat__input-wrapper">
                <p className="ai-chat__disclosure">
                  You're chatting with an AI, not a person.
                </p>
                <ChatInput
                  onSend={sendMessage}
                  onAbort={abortRequest}
                  disabled={isLoading || isConversationArchived}
                  isArchived={isConversationArchived}
                  isUploading={isUploading}
                  isOpen={isOpen}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default function AIChat() {
  const isAIChatOpen = useSelector(
    (state: RootState) => state.aiChatReducer.isAIChatOpen,
  );
  const [hasBeenOpened, setHasBeenOpened] = useState(isAIChatOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [isWide, setIsWide] = useState(false);

  useEffect(() => {
    if (isAIChatOpen) {
      setHasBeenOpened(true);
      // Mount collapsed first, then expand on the next frame so the
      // width transition runs (slide in).
      const frame = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(frame);
    }

    // Collapse (slide out) but keep mounted to preserve state.
    setIsVisible(false);
  }, [isAIChatOpen]);

  if (!hasBeenOpened) return null;

  return (
    <AIChatContent
      isOpen={isVisible}
      isWide={isWide}
      onToggleWide={() => setIsWide((prev) => !prev)}
    />
  );
}
